import {
  PROJECT_COLOR_MENU_ID,
  ProjectColorMenu,
  ToolbarAnchor,
  WireframesMenu,
} from '@components/ArtBoard/ArtboardToolbar'
import { getCanvasState } from '@components/ArtBoard/Canvas/helpers/canvas'
import type { ID } from '@octopus-do/octopus-project-core'
import { Toolbar } from '@octopus-do/octopus-storybook'
import {
  BlockAdd,
  CircleCheck,
  CircleCheckFilled,
  Image,
  ImageSlash,
  Linktopage,
  Symbol as SymbolIcon,
  SymbolRemove,
  Trash,
} from '@octopus-do/octopus-storybook/icons'
import type React from 'react'
import { useEffect } from 'react'

import { ACCEPTED_FILE_TYPES } from '@/constants/constants'
import { createSwitch } from '@/utils'
import { MORE_MENU_ID, MoreMenu } from './components'
import { useBlockToolbar } from './hooks'

interface Props {
  // The block element: the wireframes popover sits next to it.
  anchorRef: React.RefObject<HTMLDivElement | null>
  blockId: ID
  // The wireframes popover opened or closed: the block edits its rows while
  // it is open, and the toolbar hides as the former popup replaced it.
  onWireframesOpenChange: (open: boolean) => void
  wireframesOpen: boolean
}

export const BlockActionsToolbar: React.FC<Props> = ({
  anchorRef,
  blockId,
  onWireframesOpenChange,
  wireframesOpen,
}) => {
  const {
    actions,
    colorId,
    completed,
    completeDisabled,
    completeHidden,
    addBlockDisabled,
    imageAttached,
    imageInputRef,
    imageLocked,
    imageShown,
    imageUploading,
    arrowsDisabled,
    symbol,
    symbolDisabled,
    symbolLocked,
    duplicateDisabled,
    pasteDisabled,
    pasting,
    wireframes,
  } = useBlockToolbar(blockId)

  // The popover leaves with the toolbar on a deselect, without a close of
  // its own.
  useEffect(() => () => onWireframesOpenChange(false), [onWireframesOpenChange])

  const { scale } = getCanvasState(
    document.getElementById('gestureMovableContainer')
  )

  const switcher = createSwitch()
    .case(['complete'], actions.onComplete)
    .case(['add_block'], actions.onAddBlock)
    .case([PROJECT_COLOR_MENU_ID], actions.onChangeColor)
    .case(['image'], actions.onImage)
    .case(['arrows'], actions.onArrows)
    .case(['symbol'], actions.onSymbol)
    .case([MORE_MENU_ID, 'copy'], actions.onCopy)
    .case([MORE_MENU_ID, 'duplicate'], actions.onDuplicate)
    .case([MORE_MENU_ID, 'paste'], actions.onPaste)
    .case(['delete'], actions.onDelete)

  const onClick = (keys: string[]) => switcher.run(keys)

  return (
    <ToolbarAnchor hidden={wireframesOpen} scale={scale}>
      <Toolbar.Root onClick={onClick}>
        {!completeHidden && (
          <Toolbar.Button
            disabled={completeDisabled}
            icon={completed ? CircleCheckFilled : CircleCheck}
            id="complete"
            tooltip={completed ? 'Mark as not completed' : 'Mark as completed'}
          />
        )}
        <Toolbar.Button
          disabled={addBlockDisabled}
          icon={BlockAdd}
          id="add_block"
          tooltip="Add block"
        />
        <ProjectColorMenu
          colorId={colorId}
          onManage={actions.onOpenColorLibrary}
        />
        <WireframesMenu
          anchorRef={anchorRef}
          onChange={actions.onWireframesChange}
          onClose={() => onWireframesOpenChange(false)}
          onOpen={() => onWireframesOpenChange(true)}
          side="right"
          wireframes={wireframes}
        />
        {imageShown && (
          <Toolbar.Button
            icon={imageAttached ? ImageSlash : Image}
            id="image"
            loading={imageUploading}
            locked={imageLocked}
            tooltip={imageAttached ? 'Remove image' : 'Upload image'}
            variant={imageAttached ? 'danger' : 'default'}
          />
        )}
        <Toolbar.Button
          disabled={arrowsDisabled}
          icon={Linktopage}
          id="arrows"
          tooltip="Arrows"
        />
        <Toolbar.Divider />
        <Toolbar.Button
          disabled={symbolDisabled}
          icon={symbol ? SymbolRemove : SymbolIcon}
          id="symbol"
          locked={symbolLocked}
          tooltip={symbol ? 'Unlink symbol' : 'Make symbol'}
          variant={symbol ? 'danger' : 'purple'}
        />
        <Toolbar.Divider />
        <MoreMenu
          duplicateDisabled={duplicateDisabled}
          pasteDisabled={pasteDisabled}
          pasting={pasting}
        />
        <Toolbar.Button
          icon={Trash}
          id="delete"
          tooltip="Delete"
          variant="danger"
        />
      </Toolbar.Root>

      <input
        accept={ACCEPTED_FILE_TYPES.join(',')}
        hidden
        name="file"
        onChange={actions.onImageChange}
        ref={imageInputRef}
        type="file"
      />
    </ToolbarAnchor>
  )
}
